import {
  Message,
  MessageContent,
  MessageDirection,
  MessagePatch,
  MessageReactionEntry,
  MessageStatus,
  MessageType,
} from '../../../domain/conversations/types';
import { WhatsappEvent } from '../../../domain/events/types';

const MESSAGE_DIRECTIONS: MessageDirection[] = ['INBOUND', 'OUTBOUND'];

const MESSAGE_TYPES: MessageType[] = [
  'TEXT',
  'TEMPLATE',
  'IMAGE',
  'DOCUMENT',
  'VIDEO',
  'AUDIO',
  'STICKER',
  'CONTACTS',
  'REACTION',
];

const MESSAGE_STATUSES: MessageStatus[] = ['ACCEPTED', 'SENT', 'DELIVERED', 'READ', 'FAILED'];

export function mapWhatsappEvent(raw: unknown): WhatsappEvent | null {
  if (!isRecord(raw)) {
    return null;
  }

  const name = raw['event'];
  const payload = isRecord(raw['data']) ? raw['data'] : raw;

  if (
    name !== 'NEW_MESSAGE' &&
    name !== 'MESSAGE_STATUS_UPDATED' &&
    name !== 'MESSAGE_REACTION_UPDATED' &&
    name !== 'CONVERSATION_READ'
  ) {
    return null;
  }

  const companyPhoneNumber = payload['companyPhoneNumber'];
  const clientPhoneNumber = payload['clientPhoneNumber'];

  if (typeof companyPhoneNumber !== 'string' || typeof clientPhoneNumber !== 'string') {
    return null;
  }

  if (name === 'CONVERSATION_READ') {
    const readAt = payload['readAt'];

    return typeof readAt === 'string'
      ? { event: name, companyPhoneNumber, clientPhoneNumber, readAt }
      : null;
  }

  const patch = mapMessagePatch(payload['message']);

  if (!patch) {
    return null;
  }

  if (name === 'MESSAGE_STATUS_UPDATED' || name === 'MESSAGE_REACTION_UPDATED') {
    return { event: name, companyPhoneNumber, clientPhoneNumber, message: patch };
  }

  const message = asCompleteMessage(patch);

  return message ? { event: name, companyPhoneNumber, clientPhoneNumber, message } : null;
}

function mapMessagePatch(raw: unknown): MessagePatch | null {
  if (!isRecord(raw)) {
    return null;
  }

  const id = typeof raw['_id'] === 'string' ? raw['_id'] : raw['id'];

  if (typeof id !== 'string' || !id) {
    return null;
  }

  const patch: MessagePatch = { id };
  const { wamid, direction, type, content, status, failReason, messageAt } = raw;

  if (typeof wamid === 'string') {
    patch.wamid = wamid;
  }

  if (isOneOf(direction, MESSAGE_DIRECTIONS)) {
    patch.direction = direction;
  }

  if (isOneOf(type, MESSAGE_TYPES)) {
    patch.type = type;
  }

  if (isRecord(content)) {
    patch.content = content as MessageContent;
  }

  if (Array.isArray(raw['reactions'])) {
    patch.reactions = mapReactions(raw['reactions']);
  }

  if (isOneOf(status, MESSAGE_STATUSES)) {
    patch.status = status;
  }

  if (typeof failReason === 'string') {
    patch.failReason = failReason;
  }

  if (typeof messageAt === 'string') {
    patch.messageAt = messageAt;
  }

  return patch;
}

function mapReactions(raw: unknown[]): MessageReactionEntry[] {
  return raw.filter(isRecord).flatMap(entry => {
    const { emoji, direction, reactedAt } = entry;

    if (typeof emoji !== 'string' || !emoji) {
      return [];
    }

    return [
      {
        emoji,
        ...(isOneOf(direction, MESSAGE_DIRECTIONS) ? { direction } : {}),
        ...(typeof reactedAt === 'string' ? { reactedAt } : {}),
      },
    ];
  });
}

function asCompleteMessage(patch: MessagePatch): Message | null {
  const { direction, type, content, messageAt } = patch;

  if (!direction || !type || !content || !messageAt) {
    return null;
  }

  return { ...patch, direction, type, content, messageAt };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOneOf<T extends string>(value: unknown, allowed: T[]): value is T {
  return typeof value === 'string' && allowed.includes(value as T);
}
